import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import * as SelectPrimitive from '@radix-ui/react-select'
import * as SliderPrimitive from '@radix-ui/react-slider'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { Check, ChevronDown, Minus, Search, Upload, X } from 'lucide-react'
import { createContext, useContext, useId, useRef, useState } from 'react'
import { cn } from './lib/cn'

/* ==========================================================================
 * Shared field chrome
 * ======================================================================== */

interface FieldControl {
  id: string
  describedBy: string | undefined
  invalid: boolean
}

/**
 * Field publishes its wiring here so controls pick up `id`, `aria-describedby`
 * and `aria-invalid` without the caller threading them through by hand — which
 * is exactly the wiring that gets forgotten.
 */
const FieldContext = createContext<FieldControl | null>(null)

function useFieldControl(): FieldControl | null {
  return useContext(FieldContext)
}

const CONTROL_BASE = [
  'w-full rounded-control border bg-surface text-foreground',
  'placeholder:text-subtle',
  'transition-colors duration-200',
  'focus:outline-none focus:ring-2 focus:ring-focus/40 focus:border-focus',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-subtle',
].join(' ')

const CONTROL_INVALID = 'border-danger-solid/60 focus:ring-danger-solid/30 focus:border-danger-solid'

export function Label({ className, ...props }: React.ComponentProps<'label'>) {
  return (
    <label
      className={cn('block text-caption font-medium text-foreground', className)}
      {...props}
    />
  )
}

export interface FieldProps {
  label?: React.ReactNode
  /** Guidance shown below the control when there is no error. */
  hint?: React.ReactNode
  /** Error message. Its presence marks the field invalid. */
  error?: string | null
  required?: boolean
  /** Rendered inside the label row, e.g. a "Optional" chip. */
  labelSuffix?: React.ReactNode
  htmlFor?: string
  className?: string
  children: React.ReactNode
}

/**
 * Label + control + hint + error.
 *
 * The control inside inherits `id`, `aria-describedby` and `aria-invalid` from
 * context, so a Field wrapping an Input is announced correctly with no extra
 * props. Explicit props on the control still win.
 */
export function Field({
  label,
  hint,
  error,
  required,
  labelSuffix,
  htmlFor,
  className,
  children,
}: FieldProps) {
  const generated = useId()
  const id = htmlFor ?? generated
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy =
    [hint && !error ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined

  const context: FieldControl = { id, describedBy, invalid: Boolean(error) }

  return (
    <FieldContext.Provider value={context}>
      <div className={cn('space-y-1.5', className)}>
        {label ? (
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor={id}>
              {label}
              {required ? (
                <span aria-hidden className="ml-0.5 text-danger-text">
                  *
                </span>
              ) : null}
            </Label>
            {labelSuffix}
          </div>
        ) : null}

        {children}

        {hint && !error ? (
          <p id={hintId} className="text-caption text-muted">
            {hint}
          </p>
        ) : null}

        {error ? (
          <p id={errorId} role="alert" className="text-caption text-danger-text">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

/* ==========================================================================
 * Input / Textarea
 * ======================================================================== */

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'prefix'> {
  invalid?: boolean
  /** Decoration inside the control's left edge, e.g. an icon or "https://". */
  leading?: React.ReactNode
  /** Decoration inside the right edge, e.g. a unit or a copy button. */
  trailing?: React.ReactNode
}

export function Input({
  className,
  invalid,
  leading,
  trailing,
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}: InputProps) {
  const field = useFieldControl()
  const resolvedId = id ?? field?.id
  const describedBy = ariaDescribedBy ?? field?.describedBy
  const isInvalid = invalid ?? field?.invalid ?? false

  const control = (
    <input
      id={resolvedId}
      aria-describedby={describedBy}
      aria-invalid={isInvalid || undefined}
      className={cn(
        CONTROL_BASE,
        'h-10 px-3 text-sm',
        isInvalid && CONTROL_INVALID,
        leading && 'pl-9',
        trailing && 'pr-9',
        className
      )}
      {...props}
    />
  )

  if (!leading && !trailing) return control

  return (
    <div className="relative">
      {leading ? (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle [&_svg]:h-4 [&_svg]:w-4">
          {leading}
        </span>
      ) : null}
      {control}
      {trailing ? (
        <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center text-subtle">
          {trailing}
        </span>
      ) : null}
    </div>
  )
}

export interface TextareaProps extends React.ComponentProps<'textarea'> {
  invalid?: boolean
  /** Grow with content instead of showing a scrollbar. */
  autoGrow?: boolean
  /** Show a live "n / max" counter. Requires maxLength. */
  showCount?: boolean
}

export function Textarea({
  className,
  invalid,
  autoGrow = false,
  showCount = false,
  maxLength,
  value,
  onChange,
  rows = 4,
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}: TextareaProps) {
  const field = useFieldControl()
  const isInvalid = invalid ?? field?.invalid ?? false
  const [uncontrolledLength, setUncontrolledLength] = useState(
    typeof value === 'string' ? value.length : 0
  )
  const length = typeof value === 'string' ? value.length : uncontrolledLength

  return (
    <div className="relative">
      <textarea
        id={id ?? field?.id}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
        rows={rows}
        maxLength={maxLength}
        value={value}
        aria-invalid={isInvalid || undefined}
        onChange={(event) => {
          if (autoGrow) {
            const el = event.currentTarget
            el.style.height = 'auto'
            el.style.height = `${el.scrollHeight}px`
          }
          if (value === undefined) setUncontrolledLength(event.currentTarget.value.length)
          onChange?.(event)
        }}
        className={cn(
          CONTROL_BASE,
          'resize-y px-3 py-2 text-sm leading-relaxed',
          autoGrow && 'resize-none overflow-hidden',
          isInvalid && CONTROL_INVALID,
          showCount && maxLength && 'pb-6',
          className
        )}
        {...props}
      />
      {showCount && maxLength ? (
        <span
          className={cn(
            'pointer-events-none absolute bottom-1.5 right-3 font-mono text-[11px]',
            length > maxLength * 0.9 ? 'text-warning-text' : 'text-subtle'
          )}
        >
          {length} / {maxLength}
        </span>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * Select
 * ======================================================================== */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
  'aria-describedby'?: string
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  disabled,
  invalid,
  className,
  id,
  ...aria
}: SelectProps) {
  const field = useFieldControl()
  const isInvalid = invalid ?? field?.invalid ?? false

  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id ?? field?.id}
        aria-describedby={aria['aria-describedby'] ?? field?.describedBy}
        aria-invalid={isInvalid || undefined}
        className={cn(
          CONTROL_BASE,
          'flex h-10 items-center justify-between px-3 text-left text-sm',
          'data-[placeholder]:text-subtle',
          isInvalid && CONTROL_INVALID,
          className
        )}
        {...aria}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 shrink-0 text-subtle" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
            'rounded-card border border-line bg-raised shadow-overlay',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3 text-sm',
                  'text-foreground outline-none',
                  'data-[highlighted]:bg-sunken',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2.5">
                  <Check className="h-4 w-4 text-accent" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

/* ==========================================================================
 * Checkbox / Switch / Radio
 * ======================================================================== */

export interface CheckboxProps {
  checked?: boolean | 'indeterminate'
  onCheckedChange?: (checked: boolean | 'indeterminate') => void
  disabled?: boolean
  id?: string
  className?: string
  'aria-describedby'?: string
}

export function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
  ...aria
}: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border',
        'border-line-strong bg-surface transition-colors',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...aria}
    >
      <CheckboxPrimitive.Indicator className="text-accent-foreground">
        {checked === 'indeterminate' ? (
          <Minus className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Check className="h-3 w-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

/** A checkbox with its label and hint as one clickable row. */
export interface CheckboxFieldProps extends CheckboxProps {
  label: React.ReactNode
  hint?: React.ReactNode
}

export function CheckboxField({ label, hint, id, ...props }: CheckboxFieldProps) {
  const generated = useId()
  const fieldId = id ?? generated

  return (
    <div className="flex items-start gap-2.5">
      <Checkbox id={fieldId} className="mt-0.5" {...props} />
      <div className="space-y-0.5">
        <label htmlFor={fieldId} className="cursor-pointer text-sm text-foreground">
          {label}
        </label>
        {hint ? <p className="text-caption text-muted">{hint}</p> : null}
      </div>
    </div>
  )
}

export interface SwitchProps {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  id?: string
  className?: string
}

export function Switch({ checked, onCheckedChange, disabled, id, className }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'bg-border-strong data-[state=checked]:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
          'translate-x-0.5 data-[state=checked]:translate-x-[22px]'
        )}
      />
    </SwitchPrimitive.Root>
  )
}

export interface SwitchFieldProps extends SwitchProps {
  label: React.ReactNode
  hint?: React.ReactNode
}

export function SwitchField({ label, hint, id, ...props }: SwitchFieldProps) {
  const generated = useId()
  const fieldId = id ?? generated

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <label htmlFor={fieldId} className="cursor-pointer text-sm text-foreground">
          {label}
        </label>
        {hint ? <p className="text-caption text-muted">{hint}</p> : null}
      </div>
      <Switch id={fieldId} {...props} />
    </div>
  )
}

export interface RadioOption {
  value: string
  label: string
  hint?: string
  disabled?: boolean
}

export interface RadioGroupProps {
  value?: string
  onValueChange?: (value: string) => void
  options: RadioOption[]
  className?: string
  orientation?: 'vertical' | 'horizontal'
}

export function RadioGroup({
  value,
  onValueChange,
  options,
  className,
  orientation = 'vertical',
}: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      orientation={orientation}
      className={cn(orientation === 'vertical' ? 'space-y-3' : 'flex flex-wrap gap-4', className)}
    >
      {options.map((option) => {
        const id = `radio-${option.value}`
        return (
          <div key={option.value} className="flex items-start gap-2.5">
            <RadioGroupPrimitive.Item
              id={id}
              value={option.value}
              disabled={option.disabled}
              className={cn(
                'mt-0.5 h-[18px] w-[18px] shrink-0 rounded-full border border-line-strong bg-surface',
                'transition-colors',
                'data-[state=checked]:border-[6px] data-[state=checked]:border-accent',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50 focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            <div className="space-y-0.5">
              <label htmlFor={id} className="cursor-pointer text-sm text-foreground">
                {option.label}
              </label>
              {option.hint ? <p className="text-caption text-muted">{option.hint}</p> : null}
            </div>
          </div>
        )
      })}
    </RadioGroupPrimitive.Root>
  )
}

/* ==========================================================================
 * Slider
 * ======================================================================== */

export interface SliderProps {
  value?: number[]
  onValueChange?: (value: number[]) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  className?: string
  'aria-label'?: string
}

export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  className,
  ...aria
}: SliderProps) {
  return (
    <SliderPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className={cn(
        'relative flex h-5 w-full touch-none select-none items-center',
        disabled && 'opacity-50',
        className
      )}
      {...aria}
    >
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-sunken">
        <SliderPrimitive.Range className="absolute h-full bg-accent" />
      </SliderPrimitive.Track>
      {(value ?? [min]).map((_, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className={cn(
            'block h-4 w-4 rounded-full border-2 border-accent bg-surface shadow-card transition-transform',
            'hover:scale-110',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50 focus-visible:ring-offset-2'
          )}
        />
      ))}
    </SliderPrimitive.Root>
  )
}

/* ==========================================================================
 * SegmentedControl
 * ======================================================================== */

export interface SegmentedControlProps {
  value?: string
  onValueChange?: (value: string) => void
  options: SelectOption[]
  className?: string
  size?: 'sm' | 'md'
  'aria-label'?: string
}

export function SegmentedControl({
  value,
  onValueChange,
  options,
  className,
  size = 'md',
  ...aria
}: SegmentedControlProps) {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        // Radix emits '' when the active item is clicked again; keep selection.
        if (next) onValueChange?.(next)
      }}
      className={cn(
        'inline-flex items-center gap-1 rounded-control border border-line bg-sunken p-0.5',
        className
      )}
      {...aria}
    >
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            'rounded-[9px] font-medium text-muted transition-colors',
            size === 'sm' ? 'h-7 px-2.5 text-caption' : 'h-8 px-3 text-sm',
            'hover:text-foreground',
            'data-[state=on]:bg-surface data-[state=on]:text-foreground data-[state=on]:shadow-card',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {option.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  )
}

/* ==========================================================================
 * SearchInput
 * ======================================================================== */

export interface SearchInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  onClear?: () => void
}

export function SearchInput({ className, value, onClear, ...props }: SearchInputProps) {
  const hasValue = typeof value === 'string' && value.length > 0

  return (
    <Input
      type="search"
      value={value}
      leading={<Search />}
      trailing={
        hasValue && onClear ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="rounded p-1 text-subtle transition-colors hover:bg-sunken hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null
      }
      className={cn('[&::-webkit-search-cancel-button]:appearance-none', className)}
      {...props}
    />
  )
}

/* ==========================================================================
 * TagInput
 * ======================================================================== */

export interface TagInputProps {
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  /** Return an error string to reject the entry. */
  validate?: (value: string) => string | null
  max?: number
  disabled?: boolean
  className?: string
  id?: string
  'aria-describedby'?: string
}

/**
 * Free-text chips. Used for IP allowlists (CIDR ranges) and allowed databases.
 */
export function TagInput({
  value,
  onValueChange,
  placeholder = 'Type and press Enter',
  validate,
  max,
  disabled,
  className,
  id,
  ...aria
}: TagInputProps) {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = () => {
    const entry = draft.trim()
    if (!entry) return

    if (value.includes(entry)) {
      setError('Already added')
      return
    }
    if (max !== undefined && value.length >= max) {
      setError(`Maximum of ${max} reached`)
      return
    }
    const problem = validate?.(entry)
    if (problem) {
      setError(problem)
      return
    }

    onValueChange([...value, entry])
    setDraft('')
    setError(null)
  }

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          CONTROL_BASE,
          'flex min-h-10 flex-wrap items-center gap-1.5 px-2 py-1.5',
          error && CONTROL_INVALID,
          disabled && 'opacity-60',
          className
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md bg-sunken px-2 py-1 font-mono text-[12px] text-foreground"
          >
            {tag}
            <button
              type="button"
              disabled={disabled}
              aria-label={`Remove ${tag}`}
              onClick={(event) => {
                event.stopPropagation()
                onValueChange(value.filter((item) => item !== tag))
              }}
              className="rounded text-subtle transition-colors hover:text-danger-text"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id={id}
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(event) => {
            setDraft(event.currentTarget.value)
            setError(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault()
              commit()
            } else if (event.key === 'Backspace' && !draft && value.length > 0) {
              onValueChange(value.slice(0, -1))
            }
          }}
          onBlur={commit}
          className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-subtle"
          {...aria}
        />
      </div>
      {error ? (
        <p role="alert" className="text-caption text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * FileDrop
 * ======================================================================== */

export interface FileDropProps {
  onFiles: (files: File[]) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  hint?: React.ReactNode
  className?: string
  files?: File[]
  onRemove?: (index: number) => void
}

export function FileDrop({
  onFiles,
  accept,
  multiple = true,
  disabled,
  hint,
  className,
  files = [],
  onRemove,
}: FileDropProps) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className={cn('space-y-2', className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!disabled) inputRef.current?.click()
          }
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          if (disabled) return
          onFiles(Array.from(event.dataTransfer.files))
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-card border border-dashed px-6 py-8 text-center',
          'transition-colors',
          dragging ? 'border-accent bg-accent-tint/10' : 'border-line-strong bg-sunken/50',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-accent/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40'
        )}
      >
        <Upload className="h-5 w-5 text-subtle" />
        <p className="text-sm text-foreground">
          <span className="font-medium">Choose a file</span>
          <span className="text-muted"> or drag it here</span>
        </p>
        {hint ? <p className="text-caption text-muted">{hint}</p> : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          onFiles(Array.from(event.currentTarget.files ?? []))
          // Reset so choosing the same file twice still fires onChange.
          event.currentTarget.value = ''
        }}
      />

      {files.length > 0 ? (
        <ul className="space-y-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-lg bg-sunken px-3 py-2 text-caption"
            >
              <span className="truncate font-mono text-foreground">{file.name}</span>
              {onRemove ? (
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemove(index)}
                  className="rounded text-subtle transition-colors hover:text-danger-text"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * FormSection
 * ======================================================================== */

export interface FormSectionProps {
  title: string
  description?: React.ReactNode
  /** Rendered top-right — usually the section's own Save button. */
  action?: React.ReactNode
  className?: string
  children: React.ReactNode
}

/**
 * A titled block of related fields.
 *
 * Sections own their own save action rather than the page having one giant
 * form, so a failed save never discards edits made elsewhere on the page.
 */
export function FormSection({
  title,
  description,
  action,
  className,
  children,
}: FormSectionProps) {
  return (
    <section className={cn('rounded-card border border-line bg-surface shadow-card', className)}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="space-y-0.5">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {description ? <p className="text-caption text-muted">{description}</p> : null}
        </div>
        {action}
      </header>
      <div className="space-y-5 p-5">{children}</div>
    </section>
  )
}
